import { Chip, Stack, alpha, useTheme } from '@mui/material';
import { AlertOctagon, AlertTriangle, ShieldAlert, ShieldCheck } from 'lucide-react';
import { SecurityFinding } from '@/types';

export type FindingFilterType = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'ALL';

interface SecurityFindingsFilterProps {
  filter: FindingFilterType;
  setFilter: (filter: FindingFilterType) => void;
  findings: SecurityFinding[];
}

export function SecurityFindingsFilter({
  filter,
  setFilter,
  findings,
}: SecurityFindingsFilterProps) {
  const theme = useTheme();

  const filterCounts = {
    CRITICAL: findings.filter((f) => f.severity === 'critical').length,
    HIGH: findings.filter((f) => f.severity === 'high').length,
    MEDIUM: findings.filter((f) => f.severity === 'medium').length,
    LOW: findings.filter((f) => f.severity === 'low').length,
    ALL: findings.length,
  };

  const filterConfigs: {
    key: FindingFilterType;
    icon: typeof AlertOctagon | null;
    label: string;
    color: string;
  }[] = [
    {
      key: 'CRITICAL',
      icon: AlertOctagon,
      label: `CRITICAL (${filterCounts.CRITICAL})`,
      color: '#dc2626',
    },
    {
      key: 'HIGH',
      icon: ShieldAlert,
      label: `HIGH (${filterCounts.HIGH})`,
      color: '#ef4444',
    },
    {
      key: 'MEDIUM',
      icon: AlertTriangle,
      label: `MEDIUM (${filterCounts.MEDIUM})`,
      color: '#f59e0b',
    },
    {
      key: 'LOW',
      icon: ShieldCheck,
      label: `LOW (${filterCounts.LOW})`,
      color: '#22c55e',
    },
    {
      key: 'ALL',
      icon: null,
      label: `ALL (${filterCounts.ALL})`,
      color: theme.palette.primary.main,
    },
  ];

  return (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
      {filterConfigs.map(({ key, icon: Icon, label, color }) => (
        <Chip
          key={key}
          icon={Icon ? <Icon size={14} /> : undefined}
          label={label}
          onClick={() => setFilter(key)}
          size="small"
          sx={{
            px: 1,
            py: 2,
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            cursor: 'pointer',
            ...(filter === key
              ? {
                  bgcolor: color,
                  color: '#fff',
                  '& .MuiChip-icon': { color: '#fff' },
                  boxShadow: `0 2px 8px ${alpha(color, 0.4)}`,
                  transform: 'translateY(-1px)',
                  '&:hover': {
                    bgcolor: color,
                    filter: 'brightness(0.85)',
                    boxShadow: `0 4px 12px ${alpha(color, 0.5)}`,
                  },
                }
              : {
                  bgcolor: alpha(color, 0.08),
                  color: color,
                  border: `1px solid ${alpha(color, 0.2)}`,
                  '& .MuiChip-icon': { color: color },
                  '&:hover': {
                    bgcolor: alpha(color, 0.15),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 2px 6px ${alpha(color, 0.2)}`,
                  },
                }),
          }}
        />
      ))}
    </Stack>
  );
}
